import { useMemo } from "react";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";

// Builder default when a country has no configured ADM minimum (CountriesDefaultSetting.adm_min_recipients).
export const DEFAULT_MIN_RECIPIENTS = 1000;

export interface AdmCountryCapabilities {
  countryCode: string;
  // USPS-only pieces of the flow; outside the US artwork follows the country template and OPS handles codes.
  addressBoxRequired: boolean;
  canCreateInStudio: boolean;
  allowsGeoAudiences: boolean;
  allowsPerRecipientCodes: boolean;
  carrierName: string;
  minRecipients: number;
}

type CountryLike = {
  code?: string | null;
  countryDefaultSettings?: { admMinRecipients?: number | null } | null;
} | null;

/** Every country-dependent ADM rule in one place. The campaign country is the client's country. */
export const getAdmCountryCapabilities = (country: CountryLike | undefined): AdmCountryCapabilities => {
  const countryCode = country?.code || "US";
  const isUS = countryCode === "US";

  return {
    countryCode,
    addressBoxRequired: isUS,
    canCreateInStudio: isUS,
    allowsGeoAudiences: isUS,
    allowsPerRecipientCodes: isUS,
    carrierName: isUS ? "USPS" : "the postal service",
    minRecipients: country?.countryDefaultSettings?.admMinRecipients || DEFAULT_MIN_RECIPIENTS,
  };
};

export const formatMinRecipientsMessage = (minRecipients: number): string =>
  `Your selected audiences must total at least ${minRecipients.toLocaleString("en-US")} recipients.`;

// AdmBuilder renders its steps only once the client's country has loaded, so this never sees a pending country there.
export const useAdmCapabilities = (): AdmCountryCapabilities => {
  const { country } = useClientConfig();
  return useMemo(() => getAdmCountryCapabilities(country as CountryLike), [country]);
};
